import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Injector, Input, OnInit, Output, ViewChild, ViewContainerRef, ViewEncapsulation } from "@angular/core";
import { Id, IPlayerProps, IUser } from "../../../interfaces";
import { LocalStoreService, StoreSelectorsService } from "../../../services";
import { combineLatest, forkJoin, Observable, of, switchMap, catchError } from 'rxjs';
import { delay, filter, finalize, map, take, tap } from 'rxjs/operators';
import { IPlayer, IPlayerService, IScene } from '@signage/lib';
import { Compbaser } from "../../../components/compbaser";
import { MatDialog } from "@angular/material/dialog";
import {isHostDev, roundNumber} from "app/common";

@Component({
    selector: 'app-scene-preview',
    templateUrl: './scene-preview.component.html',
    styleUrls: ['./scene-preview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ScenePreviewComponent extends Compbaser implements OnInit {

    @ViewChild('component') component;
    @ViewChild('playercontainer', { read: ViewContainerRef }) entry: ViewContainerRef;

    user: IUser;
    player: IPlayer;
    playerService: IPlayerService;
    isFullFeatures: boolean;
    sceneData: IPlayerProps;
    selectedSceneId: string;
    initialLoad = true;

    scaleX = 1;
    scaleY = 1;
    sceneWidth = 1920;
    sceneHeight = 1080;
    sceneCheckeredBackground = '1';

    @Input()
    set setSelectedSceneId(i_setSelectedSceneId: Id) {
        this.selectedSceneId = i_setSelectedSceneId;
        if (!this.initialLoad && this.selectedSceneId) {
            this.initialLoad = true;
            this.setScale();
            this.cod(this.init().subscribe());
            this.cd.markForCheck();
        }
    }

    @Input()
    set setSceneWidth(i_width) {
        if (i_width) {
            this.sceneWidth = parseInt(i_width);
        }
        this.setScale();
        this.cd.markForCheck();
    }

    @Input()
    set setSceneHeight(i_height) {
        if (i_height) {
            this.sceneHeight = parseInt(i_height);
        }
        this.setScale();
        this.cd.markForCheck();
    }

    @Input() previewWidth;
    @Input() previewHeight;
    @Input() autoScale = false;
    @Input() autoPlay = false;

    @Output() sceneLoaded: EventEmitter<any> = new EventEmitter<any>();

    constructor(
        private injector: Injector,
        private storeSelector: StoreSelectorsService,
        private localStoreService: LocalStoreService,
        private cd: ChangeDetectorRef,
        protected override dialog: MatDialog,
    ) {
        super(dialog);
    }

    ngOnInit() {
        this.playerService = this.injector.get('playerService');
        this.setScale();

        this.cod(
            // bug here on pro@ms.com scene id: 27
            this.init().subscribe()
        );

        this.cod(
            this.storeSelector.getLocalStoreUpdates('ILocalStorageConfig').pipe(filter((v) => v !== null))
                .subscribe((e) => {
                    const t = this.localStoreService.getStorageValConfig('sceneCheckeredBackground');
                    if (t === this.sceneCheckeredBackground) return;
                    this.sceneCheckeredBackground = t;
                    this.cd.markForCheck();
                })
        );
    }

    setScale() {
        if (!this.sceneWidth || !this.sceneHeight) return;

        if (this.previewWidth) {
            this.scaleX = roundNumber(this.previewWidth / Math.floor(this.sceneWidth), 3);
            if (this.autoScale) {
                this.scaleY = this.scaleX;
                return;
            }
        }

        if (this.previewHeight) {
            this.scaleY = roundNumber(this.previewHeight / Math.floor(this.sceneHeight), 3);
            if (this.autoScale) {
                this.scaleX = this.scaleY;
                return;
            }
        }
    }

    init(): Observable<any> {
        return of('init').pipe(
            delay(1),
            switchMap(r => this.playerService.init()),
            take(1),
            switchMap(r => this.storeSelector.isFullFeatures()),
            take(1),
            switchMap(v => {
                this.isFullFeatures = v;
                if (this.playerService && this.playerService.setDevMode) {
                    this.playerService.setDevMode(this.isFullFeatures);
                }
                return this.storeSelector.getUser();
            }),
            take(1),
            switchMap(user => {
                this.user = user;
                this.playerService.businessDomain = this.user.businessDomain;
                this.playerService.businessId = Number(this.user.businessId);

                if (this.playerService != null) {
                    this.player = this.playerService.createScene(this.entry, null, !this.autoPlay);
                }
                // this.updateDbService();
                // return of(null);
                return forkJoin([this.updateDbService()]);
            }),
            take(1),
            catchError((i_error) => {
                if (isHostDev()){
                    alert(`scene preview failed: ${i_error}`);
                }
                console.log(`scene preview failed: ${i_error}`);
                return of(null);
            }),
            finalize(() => {
                if (this.player != null) {
                    this.playerService.removeScene(this.entry, this.player);
                    this.playerService = null;
                    this.player = null;
                }
            })
        );
    }

    updateDbService(): Observable<any> {
        const $resources = this.storeSelector.getResources(false);
        const $scenes = this.storeSelector.getScenes(null, true);
        const $schedules = this.storeSelector.getSchedules();

        return combineLatest([/*$sceneData,*/ $resources, $scenes, $schedules]).pipe(
            map(([i_resources, i_scenes, i_schedules]) => ({
                i_resources,
                i_scenes,
                i_schedules
            })),
            switchMap(v => {
                if (!v || !v.i_scenes || !v.i_resources || v.i_scenes.length == 0) return of(null);
                if (!this.playerService) return of(null);

                // this.sceneData = v.i_sceneData;

                /** Load dynamic scene component first time */
                v.i_scenes.forEach((i_scene) => {
                    const sceneId = i_scene.Player.$.id;
                    if (sceneId == this.selectedSceneId) {
                        this.sceneData = i_scene;
                        const sceneLayout = i_scene.Player.Data[0].Layout[0].$;
                        this.sceneWidth = +sceneLayout.width;
                        this.sceneHeight = +sceneLayout.height;
                        this.setScale();
                    }
                    this.playerService.caching.setRecord('scenes', Number(sceneId),
                        of({
                            json: i_scene,
                            changelist_id: i_scene.Player.$.changelist_id
                        })
                    );
                });

                if (!this.sceneData) return of(null);

                v.i_resources.forEach(resource => {
                    this.playerService.caching.setRecord('resources', Number(resource.resource_id), of(resource));
                });

                v.i_schedules.forEach(schedule => {
                    this.playerService.caching.setRecord('schedules', Number(schedule.schedule_id), of(schedule));
                });

                if (this.initialLoad) {
                    this.initialLoad = false;
                    return (this.player as IScene).update(this.sceneData.Player, 'rebuild', []).pipe(
                        tap({
                            next: value => {
                                if (value == 'update-ready') {
                                    if (this.playerService != null) {
                                        //??? this.playerService.pixi.renderer.resize(this.player.actualWidth, this.player.actualHeight);
                                        this.sceneLoaded.emit(this.selectedSceneId);
                                    }
                                }
                            }
                        })
                    );
                }
                return of(null);
            })
        );
    }

    // destroy(): void {
    //     super.destroy();
    // }
}
